import { useEffect, useRef, useState } from 'react';

interface WhyItem {
  icon: string;
  text: string;
  sub: string;
}

interface WhyChooseUsProps {
  title?: string;
  subtitle?: string;
  items?: WhyItem[];
}

const defaultItems: WhyItem[] = [
  {
    icon: 'ri-vip-diamond-line',
    text: 'Premium-Grade Window Films',
    sub: 'We source only professional-tier tint films — no generic or low-grade materials that bubble, fade, or peel.',
  },
  {
    icon: 'ri-focus-2-line',
    text: 'Precision Computer-Cut Installation',
    sub: 'Templates cut to your exact vehicle, window, or glass dimensions — installed by trained tint technicians.',
  },
  {
    icon: 'ri-eye-line',
    text: 'Flawless Bubble-Free Finish',
    sub: 'Every tint job is inspected to ensure zero bubbles, perfect edges, and clean alignment before delivery.',
  },
  {
    icon: 'ri-time-line',
    text: 'Fast, Reliable Turnaround',
    sub: 'Most automotive tinting is completed same-day. Residential and commercial projects are scheduled to suit you.',
  },
];

const stats = [
  { value: '500+', label: 'Windows Tinted' },
  { value: '5.0★', label: 'Average Rating' },
  { value: '10+', label: 'Years in Philadelphia' },
];

export default function WhyChooseUs({
  title = 'Why Clients Choose\nTINTSPRO X.',
  subtitle = 'From first-time vehicle tinting to full commercial building film — every project gets the same professional attention and premium results.',
  items = defaultItems,
}: WhyChooseUsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#080808] py-0 overflow-hidden" ref={ref}>
      <div className="flex flex-col lg:flex-row min-h-[680px]">
        {/* Left image with stats */}
        <div
          className={`relative w-full lg:w-[48%] h-[440px] lg:h-auto overflow-hidden shrink-0 transition-all duration-[1000ms] ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/f69ae00eb3776e9b7bafaef549c36b17.jpg"
            alt="TINTSPRO X professional tinting technician"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#080808]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/80 via-transparent to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-8 flex items-center justify-between">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-serif text-[#AAFF00] text-3xl font-bold leading-none mb-1">{stat.value}</p>
                <p className="text-white/50 text-xs font-sans">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right content */}
        <div
          className={`flex-1 bg-[#0f0f0f] border-l border-white/5 px-8 md:px-14 lg:px-16 py-16 md:py-20 flex flex-col justify-center transition-all duration-[1000ms] delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-[#AAFF00]" />
            <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Why Choose Us</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-5 whitespace-pre-line">
            {title}
          </h2>
          <p className="text-white/45 font-sans text-base leading-relaxed mb-10 max-w-md">{subtitle}</p>

          <div className="space-y-6">
            {items.map((item, i) => (
              <div
                key={i}
                className={`flex gap-5 transition-all duration-700 ${
                  visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
                style={{ transitionDelay: `${300 + i * 100}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#AAFF00]/10 border border-[#AAFF00]/15 shrink-0 mt-0.5">
                  <i className={`${item.icon} text-[#AAFF00] text-lg`}></i>
                </div>
                <div>
                  <p className="text-white font-sans font-semibold text-base leading-tight mb-1">{item.text}</p>
                  <p className="text-white/40 text-sm font-sans leading-relaxed">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#estimate"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-7 py-3.5 rounded-full hover:bg-[#C4FF33] transition-colors duration-200 cursor-pointer"
            >
              Book a Free Consultation
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
            <a
              href="tel:2155949404"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/12 text-white/60 text-sm font-sans font-medium px-7 py-3.5 rounded-full hover:border-white/30 hover:text-white transition-all duration-200 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-[#AAFF00] text-sm"></i>
              </div>
              (215) 594-9404
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
